// notificationRulesApi — typed CRUD for /v1/notifications/rules.
//
// Backed by apps/notification-api/src/notification-rules.ts. The rule
// editor on /dashboard/line uses these helpers; the same endpoints are
// also consumed by the (future) daily summary cron so editing here
// actually changes who gets the morning briefing.

import { apiFetch } from "../../lib/api-client.js";

export type TriggerKind =
  | "overdue" | "incident" | "daily" | "handoff" | "system"
  | "coverage_gap" | "schedule_change" | "submission_response";

export type ChannelKind = "line" | "email" | "sms";

export type RuleStatus = "active" | "muted" | "disabled";

export type RecipientRole =
  | "owner" | "manager" | "officer" | "tenant_admin" | "platform_admin"
  | "incident_reporter" | "auditor";

export interface NotificationRuleRow {
  id: string;
  tenant_id: string;
  site_id: string | null;
  trigger_kind: TriggerKind | string;
  event_type: string | null;
  channel_kind: ChannelKind | string;
  recipient_role: RecipientRole | string | null;
  template: string | null;
  escalate_after_seconds: number | null;
  config_json: string;
  status: RuleStatus | string;
  created_at: string;
  updated_at: string | null;
}

export interface NotificationRulesResponse { items: NotificationRuleRow[]; correlation_id: string | null }

export class NotificationRulesApiError extends Error {
  status: number; code: string; correlationId: string | null; fields?: string[];
  constructor(status: number, code: string, correlationId: string | null, fields?: string[]) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.fields = fields;
  }
}

export function friendlyRulesError(err: unknown): string {
  if (err instanceof NotificationRulesApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能管理通知規則。";
    if (err.status === 404) return "找不到此通知規則。";
    if (err.status === 400 && err.code === "invalid_body") {
      return `欄位不正確：${(err.fields ?? []).join("、") || "請檢查輸入"}。`;
    }
    return `通知規則操作失敗（${err.code}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<NotificationRulesApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let fields: string[] | undefined;
  try { const b = (await r.json()) as { error?: string; correlation_id?: string; fields?: string[] }; code = b.error ?? code; cid = b.correlation_id ?? cid; fields = b.fields; } catch {}
  return new NotificationRulesApiError(r.status, code, cid, fields);
}

export interface ListRulesQuery {
  tenantId?: string | null;
  triggerKind?: TriggerKind | null;
  channelKind?: ChannelKind | null;
  status?: RuleStatus | null;
}
export async function listNotificationRules(q: ListRulesQuery = {}): Promise<NotificationRulesResponse> {
  const qs = new URLSearchParams();
  if (q.tenantId) qs.set("tenant_id", q.tenantId);
  if (q.triggerKind) qs.set("trigger_kind", q.triggerKind);
  if (q.channelKind) qs.set("channel_kind", q.channelKind);
  if (q.status) qs.set("status", q.status);
  const r = await apiFetch(`/v1/notifications/rules${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as NotificationRulesResponse;
}

export interface CreateRuleBody {
  trigger_kind: TriggerKind;
  channel_kind?: ChannelKind;
  site_id?: string | null;
  recipient_role?: RecipientRole | null;
  template?: string | null;
  escalate_after_seconds?: number | null;
  /** Open bag for trigger-specific knobs. Common keys:
   *  - `severity: "info"|"low"|"med"|"high"|"critical"` for incident rules
   *  - `mute_until: <iso>` to temporarily silence */
  config_json?: Record<string, unknown>;
  status?: RuleStatus;
}
export async function createNotificationRule(body: CreateRuleBody, tenantId?: string | null): Promise<{ id: string }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/notifications/rules${qs}`, {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { id: string };
}

export type PatchRuleBody = Partial<CreateRuleBody>;
export async function patchNotificationRule(id: string, body: PatchRuleBody, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/notifications/rules/${encodeURIComponent(id)}${qs}`, {
    method: "PATCH", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}

export async function disableNotificationRule(id: string, tenantId?: string | null): Promise<void> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/notifications/rules/${encodeURIComponent(id)}${qs}`, {
    method: "DELETE", credentials: "include",
  });
  if (!r.ok) throw await asError(r);
}
